import React, { FunctionComponent, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router";
import DualPageLayout from "@components/layouts/DualPageLayout";
import ResourceFormField from "@components/inputs/ResourceFormField";
import { Form, Spin } from "antd";
import {
  ResourceInputList,
  ResourceInputSection,
} from "@type/estimateInput/estimateInput.response";
import {
  ContactDto,
  ResourceInputDto,
  ResourceInputGroup,
  InputField,
} from "@type/estimateInput/estimateInput.dto";
import BasicButton from "@components/buttons/BasicButton";
import Results from "@components/result/Results";
import { EstimateResultsResponseDto } from "@type/estimateResults/estimateResults.response";

import CollectivityResources from "src/static/data/example/collectivity-resources.json";
import BasicDivider from "@components/divider/BasicDivider";
import {
  ShareAltOutlined,
  DownloadOutlined,
  LeftOutlined,
} from "@ant-design/icons";
import { useHistory, useLocation } from "react-router-dom";
import { useViewport } from "@utils/breakpoints-helper";
import ShareDrawer from "@components/drawers/ShareDrawer";
import SaveDrawer from "@components/drawers/SaveDrawer";
import BasicCard from "@components/cards/BasicCard";
import {
  requestCollectivityComputeSimulation,
  requestLoadCollectivitySimulation,
  requestCollectivitySaveSimulation,
} from "@state/em_simulation/SimulationEffects";
import { requestGetCollectivityResources } from "@state/em_results/EMResourceEffects";
import { toastError } from "@utils/toast-helper";
import { getCorrectSiteKey } from "@utils/captcha-helper";
import {GoogleReCaptchaProvider} from "@utils/react-google-recaptcha-v3/google-recaptcha-provider";

const CollectivityScreen: FunctionComponent<RouteComponentProps> = () => {
  const [form] = Form.useForm();
  const [activeSide, setActiveSide] = useState<"left" | "right">("left");
  const [resourceSections, setResourceSections] = useState<
    ResourceInputSection[]
  >([]);
  const [inputList, setInputList] = useState<ResourceInputGroup>([]);
  const [results, setResults] = useState<EstimateResultsResponseDto>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const location = useLocation();
  const [currentSimulationId, setCurrentSimulationId] = useState<string>();
  const [simulationInputDto, setSimulationInputDto] = useState<
    ResourceInputDto[]
  >([]);
  const history = useHistory();
  const { width } = useViewport();
  const [isShareDrawerVisible, setIsShareDrawerVisible] =
    useState<boolean>(false);
  const [isSaveDrawerVisible, setIsSaveDrawerVisible] =
    useState<boolean>(false);
  const [delayedComputeFn, setDelayedComputeFn] = useState<
    NodeJS.Timeout | undefined
  >(undefined);

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = () => {
    void requestGetCollectivityResources({}).then((result) => {
      if (result.ok && result.data) {
        const resourceList = result.data;
        if (
          (CollectivityResources as ResourceInputList).type === "collectivity"
        ) {
          const inputs = [];
          for (const section of resourceList.sections) {
            for (const resource of section.content) {
              inputs.push({
                key: resource.key,
                value: undefined,
                unit: undefined,
              });
            }
          }
          setResourceSections(resourceList.sections);
          setInputList(inputs);
          setIsLoading(false);
        }
      }
    });
  };

  useEffect(() => {
    const simulationId = new URLSearchParams(location.search).get("id");
    if (simulationId) {
      void requestLoadCollectivitySimulation({ id: simulationId }).then(
        (result) => {
          if (result.ok && result.data) {
            if (result.data.result?.type === "COLLECTIVITY") {
              if (result.data.inputs) {
                addInputValues(result.data.inputs);
              }
              if (result.data.result) {
                setResults(result.data.result);
              }
            } else {
              toastError(
                "Une erreur est survenue lors de la récupération de votre simulation."
              );
            }
          }
        }
      );
    }
  }, [location]);

  const getLeftFooterActions = () => {
    const valid = inputList.some(
      (input) => (form.getFieldValue(input.key) as InputField)?.value
    );
    return (
      <div className="side-panel-footer-content d-flex flex-row justify-content-between align-items-center">
        <a
          className="button-link text-sm font-weight-bold text-decoration-none d-flex align-items-center p-0 me-2"
          onClick={history.goBack}
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              history.goBack();
            }
          }}
        >
          <LeftOutlined />
          <div className="ms-2">Retour</div>
        </a>
        {width < 768 && (
          <BasicButton
            className="button-icon ms-2"
            variant="secondary"
            onClick={() => setActiveSide("right")}
            text="Voir les résultats"
            disabled={!valid}
          />
        )}
      </div>
    );
  };

  const getHeaderActions = () => {
    if (!results) return undefined;
    return (
      <div className="d-flex flex-row align-items-center justify-content-center justify-content-md-end w-100">
        <div className="d-flex flex-row align-items-center d-md-none">
          {activeSide === "right" && (
            <BasicButton
              className="button-icon collapsible-button-xs"
              variant="white"
              onClick={() => {
                setActiveSide("left");
              }}
              text={"Modifier la simulation"}
              icon={<LeftOutlined />}
            />
          )}
          <BasicDivider variant="grey" width={1} height={32} className="mx-3" />
        </div>
        <BasicButton
          className="button-icon collapsible-button me-3"
          variant="white"
          onClick={() => setIsShareDrawerVisible(true)}
          text={"Partager votre simulation"}
          icon={<ShareAltOutlined />}
          size="lg"
        />
        <BasicButton
          className="button-icon collapsible-button"
          variant="secondary"
          onClick={() => setIsSaveDrawerVisible(true)}
          text={"Enregistrer votre simulation"}
          icon={<DownloadOutlined />}
          size="lg"
        />
        <GoogleReCaptchaProvider
          reCaptchaKey={getCorrectSiteKey()}
          language="fr"
          scriptProps={{
            async: false, // optional, default to false,
            defer: false, // optional, default to false
            appendTo: "head", // optional, default to "head", can be "head" or "body",
            nonce: undefined, // optional, default undefined
          }}
        >
          <ShareDrawer
            source={"COLLECTIVITY"}
            visible={isShareDrawerVisible}
            simulationId={currentSimulationId}
            saveSimulation={handleSaveSimulationWithContactDto}
            onUpdateSimulationId={setCurrentSimulationId}
            onClose={() => setIsShareDrawerVisible(false)}
          />
          <SaveDrawer
            isCollectivity={true}
            visible={isSaveDrawerVisible}
            saveSimulation={handleSaveSimulationWithContactDto}
            onUpdateSimulationId={setCurrentSimulationId}
            onClose={() => setIsSaveDrawerVisible(false)}
          />
        </GoogleReCaptchaProvider>
      </div>
    );
  };

  const getResourceByKey = (key: string) => {
    for (const section of resourceSections) {
      for (const resource of section.content) {
        if (resource.key === key) return resource;
      }
    }
    return null;
  };

  const handleSaveSimulationWithContactDto = (
    contactDto: ContactDto,
    successCallback: (simulationId: string) => void,
    errorCallback: () => void
  ) => {
    const simulationSaveDto = {
      inputs: simulationInputDto,
      contact: contactDto,
    };

    requestCollectivitySaveSimulation({ dto: simulationSaveDto })
      .then((result) => {
        if (result && result.ok) {
          if (result.data) {
            successCallback(result.data.id);
          } else {
            errorCallback();
            toastError(
              "Une erreur est survenue lors de la sauvegarde de votre simulation"
            );
          }
        } else {
          errorCallback();
          toastError(
            "Une erreur est survenue lors de la sauvegarde de votre simulation"
          );
        }
      })
      .catch(() => {
        errorCallback();
        toastError(
          "Une erreur est survenue lors de la sauvegarde de votre simulation"
        );
      });
  };

  const renderResourceInputs = inputList.map((input, index) => {
    const resource = getResourceByKey(input.key);
    if (resource)
      return (
        <React.Fragment key={`resources-input-${input.key}`}>
          {index > 0 && (
            <BasicDivider
              variant="grey"
              height={1}
              className={"w-100 mt-4 mb-4"}
            />
          )}
          <Form.Item
            name={input.key}
            className="collectivity-resource-form-item"
          >
            <ResourceFormField
              resource={resource}
              module={"collectivity"}
              onChange={() => {
                validateForm();
              }}
            />
          </Form.Item>
        </React.Fragment>
      );
    else return <></>;
  });

  const buildInputDto = () => {
    const simulationInputsDto: ResourceInputDto = {};
    inputList.forEach((input) => {
      const value = (form.getFieldValue(input.key) as InputField)?.value;
      if (value)
        simulationInputsDto[input.key] = {
          value: (form.getFieldValue(input.key) as InputField)?.value,
          unit: (form.getFieldValue(input.key) as InputField)?.unit,
        };
    });
    return [simulationInputsDto];
  };

  const addInputValues = (inputDtoList: ResourceInputDto[]) => {
    const inputGroup = inputDtoList[0];
    Object.keys(inputGroup).forEach((key) => {
      const value = inputGroup[key];
      form.setFieldsValue({ [key]: value });
    });
  };

  const computeResults = () => {
    if (delayedComputeFn) clearTimeout(delayedComputeFn);

    const delayDebounceFn = setTimeout(() => {
      const inputsDto = buildInputDto();
      setSimulationInputDto(inputsDto);
      requestCollectivityComputeSimulation({ dto: inputsDto });
    }, 500);
    setDelayedComputeFn(delayDebounceFn);
  };

  useEffect(() => {
    return requestCollectivityComputeSimulation.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setResults(result.data);
      }
    });
  });

  const validateForm = () => {
    const valid = inputList.some(
      (input) => (form.getFieldValue(input.key) as InputField)?.value
    );
    if (valid) {
      computeResults();
      if (width >= 768) {
        setActiveSide("right");
      }
    }
  };

  return (
    <DualPageLayout
      container
      noHeader={width < 768 && activeSide === "left"}
      headerActions={getHeaderActions()}
      leftBodyClassName={`side-panel-collectivity ${
        activeSide === "left" ? "active-panel" : ""
      }`}
      noFooter
      leftFooterActions={getLeftFooterActions()}
      leftContent={
        <>
          <BasicCard className="collectivity-inputs-card">
            <>
              <div className="collectivity-inputs-card-header">
                Décrivez votre territoire
              </div>
              {isLoading ? (
                <div className="h-100 py-3 d-flex align-items-center justify-content-center">
                  <Spin />
                </div>
              ) : (
                <Form
                  form={form}
                  initialValues={inputList}
                  // validateTrigger={["onBlur"]}
                  onBlur={validateForm}
                >
                  {renderResourceInputs}
                </Form>
              )}
            </>
          </BasicCard>
        </>
      }
      rightBodyClassName={`d-flex w-100 ${
        activeSide === "right" ? "active-panel" : null
      }`}
      rightContent={
        <Results
          title={"Estimation de votre potentiel de production"}
          type={"collectivity"}
          results={results}
          onShare={() => {
            setIsShareDrawerVisible(true);
          }}
          onSave={() => {
            setIsSaveDrawerVisible(true);
          }}
        />
      }
    />
  );
};

export default CollectivityScreen;
